import { L1Measurement } from "./L1Measurement"
import { LabelSet } from "./LabelSet"
import { ManualMeasurement } from "./ManualMeasurement"
import { Patient } from "./Patient"
import { PatientNote } from "./PatientNote"
import { User } from "./User"
import { isValidFieldValue } from "../utils/noteUtils"

export enum LabelType {
    NotSpecified = 0,
    HeartMeasurement = 1,
    NeckMeasurement = 2,
    AxillaMeasurement = 3,
    EarMeasurement = 4,
    ComprehensiveMeasurement = 5,
    AutonomousMeasurement = 6,
    Prescription = 7,
    ClinicConversation = 8,
    ClinicConversationChunk = 9,
    ButtonRecording = 10,
    DictationAudio = 11,
    PhoneCallAudio = 12,
    CombinedClinicConv = 13,
    DocumentSoap = 14,
    AudioButtonRecording = 15,
    AggregateLabel = 16,
    NxMIC = 17,
    Placeholder = 18,
    SampleConversation = 19,
    Temperature = 20,
    NxHubBatch = 21,
    WalkieTalkieAudio = 22,
}

export type RecordingTypeInfo = {
    label: string;
    icon: string;
    color: string;
    bgColor: string;
};

export const RECORDING_TYPES: Record<string, RecordingTypeInfo> = {
    'HeartMeasurement': {
        label: 'Heart Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'NeckMeasurement': {
        label: 'Neck Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'AxillaMeasurement': {
        label: 'Axilla Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'EarMeasurement': {
        label: 'Ear Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'ComprehensiveMeasurement': {  
        label: 'Comprehensive Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'AutonomousMeasurement': {
        label: 'Autonomous Measurement',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'Prescription': {
        label: 'Prescription',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'ClinicConversation': {
        label: 'Clinic Conversation',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'ButtonRecording': {
        label: 'Button Recording',
        icon: 'microchip',
        color: 'text-purple-700',
        bgColor: 'bg-purple-100'
    },
    'DictationAudio': {
        label: 'Dictation Audio',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'PhoneCallAudio': {
        label: 'Phone Call Audio',
        icon: 'phone',
        color: 'text-orange-700',
        bgColor: 'bg-orange-100'
    },
    'CombinedClinicConv': {
        label: 'Audio Recording',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'DocumentSoap': {
        label: 'Document SOAP',
        icon: 'file-alt',
        color: 'text-green-700',
        bgColor: 'bg-green-100'
    },
    'AudioButtonRecording': {
        label: 'Audio Button Recording',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'AggregateLabel': {
        label: 'Aggregate Label',
        icon: 'file-medical-alt',
        color: 'text-red-700',
        bgColor: 'bg-red-100'
    },
    'NxMIC': {
        label: 'NxMIC',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'Placeholder': {
        label: 'Placeholder',
        icon: 'file-alt',
        color: 'text-gray-700',
        bgColor: 'bg-gray-100'
    },
    'SampleConversation': {
        label: 'Sample Conversation',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'Temperature': {
        label: 'Temperature',
        icon: 'thermometer',
        color: 'text-red-700',
        bgColor: 'bg-red-100'
    },
    'NxHubBatch': {
        label: 'NxHub Batch',
        icon: 'microphone',
        color: 'text-blue-700',
        bgColor: 'bg-blue-100'
    },
    'WalkieTalkieAudio': {
        label: 'Walkie-Talkie Audio',
        icon: 'walkie-talkie',
        color: 'text-teal-700',
        bgColor: 'bg-teal-100'
    },
};

export class Label {
    id: string = ""
    name: string = ""
    fromTime: number = 0
    toTime?: number = 0
    color: string | null = null
    description: string = ""
    labelSetId: string = ""
    labelSet: LabelSet | null = null
    patientId: string = ""
    patient: Patient | null = null
    type: keyof typeof LabelType  = "NotSpecified"
    l1Measurements: L1Measurement[] | null = null
    ownedPatientNotes: PatientNote[] | null = null
    createdBy: User | null = null
    medicalTemplateId?: string = undefined
    metadata?: Record<string, any> = {}
    manualMeasurements: ManualMeasurement[] | null = null
    isCreatedByOnboarding?: boolean = false
    status: string = "Todo"
    deviceSerial?: string = undefined
    friendlyName?: string | null = undefined
    aggregatedFromLabels?: Label[] | null = null

    constructor(init?: Partial<Label & { metadata: string | Record<string, any> }>) {
        if (!init) {
            return
        }

        Object.assign(this, init);

        if (init.labelSet) {
            this.labelSet = new LabelSet(init.labelSet)
        }

        if (init.patient) {
            this.patient = new Patient(init.patient)
        }

        if (init.l1Measurements) {
            this.l1Measurements = init.l1Measurements.map(measurement => new L1Measurement(measurement));
        }

        if (init.ownedPatientNotes) {
            this.ownedPatientNotes = init.ownedPatientNotes.map((note: PatientNote) => new PatientNote(note))
        }

        if (init.createdBy) {
            this.createdBy = new User(init.createdBy);
        }

        if (init.manualMeasurements) {
            this.manualMeasurements = init.manualMeasurements.map(measurement => new ManualMeasurement(measurement));
        }

        if (init.metadata) {
            // Metadata can arrive in three shapes:
            //   - an already-parsed object,
            //   - a JSON string,
            //   - a *double-encoded* JSON string ('"{\\"k\\":\\"v\\"}"').
            // It can also arrive malformed (truncated, log-corrupted). We must
            // not throw here — Label is constructed inside react-query
            // fetchers; an exception fails the entire query and downstream
            // consumers (e.g. Soundbook's tolerant ClinicalSummary builder)
            // never get to do their fallback work. Best effort: try to parse,
            // and on failure leave metadata as an empty object so callers see
            // "no metadata" rather than a crash.
            if (typeof init.metadata === 'string') {
                try {
                    // Double-encoded case: leading quote means the value is a
                    // JSON-string containing JSON. Unwrap once, then parse
                    // again if we still hold a string.
                    let parsed: any = init.metadata[0] === '"'
                        ? JSON.parse(init.metadata)
                        : init.metadata;
                    if (typeof parsed === 'string') {
                        parsed = JSON.parse(parsed);
                    }
                    this.metadata = parsed && typeof parsed === 'object' ? parsed : {};
                } catch {
                    this.metadata = {};
                }
            } else {
                this.metadata = init.metadata;
            }
        }

        if (init.aggregatedFromLabels) {
            this.aggregatedFromLabels = init.aggregatedFromLabels.map(label => new Label(label));
        }
    }

    toDevicePageUrl(): string {
        return `/devices/${this.labelSet?.device?.id}`;
    }

    toDataPageUrl(): string {
        const searchParam = [];
        if (this.fromTime) {
            searchParam.push(`fromTime=${+this.fromTime}`);
        }
        if (this.toTime) {
            searchParam.push(`toTime=${+this.toTime}`);
        }
        return `/devices/${this.labelSet?.device?.serial}/labels/${this.id}/data?latestSec=14400&${searchParam.join('&')}`;
    }

    // Label types whose payload is spoken audio: their detail page plays the
    // AudioRecording channel and their patient note is a true transcript (not
    // an AI summary of sensor data).
    get isAudioRecording(): boolean {
        return this.type === "ClinicConversation" || this.type === "DictationAudio" || this.type === "PhoneCallAudio" || this.type === "CombinedClinicConv" || this.type === "AudioButtonRecording" || this.type === "NxMIC" || this.type === "NxHubBatch" || this.type === "WalkieTalkieAudio";
    }

    // A server-synthesized announcement clip (the code-alert TTS pipeline): a
    // WalkieTalkieAudio label whose metadata carries the spoken transcript —
    // the same signature AnnouncementTranscript keys on. The backend anchors
    // these on an arbitrary recipient device for storage (a label must belong
    // to some device's set), so device attribution in lists must not read as
    // "this device spoke the clip". Gated on the label TYPE, not just the
    // metadata key: metadata is free-form and other pipelines write
    // `transcript` into other label types.
    get isSystemAnnouncement(): boolean {
        return this.type === "WalkieTalkieAudio"
            && typeof this.metadata?.transcript === 'string'
            && this.metadata.transcript.trim().length > 0;
    }

    toDetailPageUrl(): string {
        const searchParam = [];
        if (this.fromTime) {
            searchParam.push(`fromTime=${+this.fromTime}`);
        }
        if (this.toTime) {
            searchParam.push(`toTime=${+this.toTime}`);
        }
        if (this.isAudioRecording) {
            searchParam.push(`channel=AudioRecording`);
        }
        if (this.type === "DocumentSoap") {
            searchParam.push(`channel=Document`);
        }
        return `/labels/${this.id}/detail?latestSec=14400&${searchParam.join('&')}`;
    }


    get soapSummary(): string | undefined {
        try {
            // Parse metadata if it's a string, otherwise use as-is
            let parsedMetadata: any;
            if (typeof this.metadata === 'string') {
                parsedMetadata = JSON.parse(this.metadata);
            } else {
                parsedMetadata = this.metadata;
            }

            // Access petData from the parsed metadata
            const petData = parsedMetadata?.petData;
            const animalName = petData?.animal_name?.value;
            const animalNameExists = petData?.animal_name?.exists;
            const summary = petData?.summary?.value;
            
            // Only show animalName if the patient is a default patient and animalName exists and is not null/empty
            const isDefaultPatient = this.patient?.isDefault;
            const hasValidAnimalName = animalNameExists && isValidFieldValue(animalName);
            if (!isValidFieldValue(summary)) return undefined;
            return (isDefaultPatient && hasValidAnimalName) ? `${animalName}: ${summary}` : summary as string;
        } catch (error) {
            console.error("Error parsing SOAP note content:", error);
            return undefined;
        }
    }

    get vetOrStaffName(): string | undefined {
        try {
            let parsedMetadata: any;
            if (typeof this.metadata === 'string') {
                parsedMetadata = JSON.parse(this.metadata);
            } else {
                parsedMetadata = this.metadata;
            }

            const petData = parsedMetadata?.petData;
            const vet = petData?.veterinarian?.value;
            const staff = petData?.staff_members?.value;

            if (isValidFieldValue(vet)) return (vet as string).trim();
            if (isValidFieldValue(staff)) return (staff as string).trim();
            return undefined;
        } catch {
            return undefined;
        }
    }
}
